import { useState } from "react";
import { FiCalendar, FiHome, FiEdit3, FiX, FiFileText } from "react-icons/fi";
import { FaUserTie, FaClock as FaClockSolid } from "react-icons/fa";
import { StatusDropdown } from "../../components/common/StatusComponents";
import Swal from "sweetalert2";
import FilterBar from "../../components/common/FilterBar";
import useDateRangeFilter from "../common/useDateRangeFilter";
import { ALL_RANGE_OPTIONS } from "../common/dateRange";
import BoardingActivitySummaryDrawer from "../../components/common/BoardingActivitySummaryDrawer";
import GroomingActivitySummaryDrawer from "../../components/common/GroomingActivitySummaryDrawer";
const getAllServices = (appointments, boarding) => {
  return [...appointments, ...boarding].filter((service) => service != null);
};

const normalizeServiceStatus = (status) => {
  const s = status?.toLowerCase();
  if (s === "confirmed") return "confirmed";
  if (s === "started") return "started";
  if (s === "completed") return "completed";
  if (s === "cancel" || s === "cancelled") return "cancelled";
  if (s === "pending") return "pending";
  if (s === "approved") return "approved";
  if (s === "rejected") return "rejected";
  return "booked";
};

const STATUS_FILTER_OPTIONS = [
  { key: "", label: "All" },
  { key: "confirmed", label: "Confirmed" },
  { key: "started", label: "Started" },
  { key: "cancelled", label: "Cancelled" },
  { key: "completed", label: "Completed" },
];

const Appointments = ({
  mode = "upcoming",
  petData,
  clientAppointments = [],
  clientBoarding = [],
  clientOrders = [],
  onEditAppointment,
  onDeleteAppointment,
  onStatusUpdate,
  onOpenAddons,
  setEditingService,
  setShowPetServiceDrawer,
}) => {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedBoardingBooking, setSelectedBoardingBooking] = useState(null);

  const {
    dateFilterState,
    activeRange,
    handleDateChange,
    handleRangeChange,
    handleClearDateFilter,
  } = useDateRangeFilter("all", () => {});
  const getPetAppointmentsAndBoarding = () => {
    if (!petData || !petData._id) return [];

    const allServices = [...clientAppointments, ...clientBoarding].filter(
      (s) => s != null,
    );

    const filteredServices = allServices.filter((service) => {
      if (!service || Object.keys(service).length === 0) return false;

      if (service.items && Array.isArray(service.items)) {
        const hasMatchingPet = service.items.some((item) => {
          const boardingPetName = item.boarding_info?.pet_details?.name;
          const itemPetName = item.pet_name;
          const rawPetName = item.pet_details?.name;
          const appointmentPetName = item.appointment_info?.pet_details?.name;
          return (
            boardingPetName?.toLowerCase() === petData.name.toLowerCase() ||
            itemPetName?.toLowerCase() === petData.name.toLowerCase() ||
            rawPetName?.toLowerCase() === petData.name.toLowerCase() ||
            appointmentPetName?.toLowerCase() === petData.name.toLowerCase()
          );
        });

        if (hasMatchingPet) {
          service.item = service.items.find((item) => {
            const boardingPetName = item.boarding_info?.pet_details?.name;
            const itemPetName = item.pet_name;
            const rawPetName = item.pet_details?.name;
            const appointmentPetName = item.appointment_info?.pet_details?.name;
            return (
              boardingPetName?.toLowerCase() === petData.name.toLowerCase() ||
              itemPetName?.toLowerCase() === petData.name.toLowerCase() ||
              rawPetName?.toLowerCase() === petData.name.toLowerCase() ||
              appointmentPetName?.toLowerCase() === petData.name.toLowerCase()
            );
          });
          service.orderNumber = service.order_number;
          service.details =
            service.item?.boarding_info?.boarding_services?.name ||
            service.item?.service_name ||
            "Boarding Service";
          return true;
        }
      }

      const details = service.details || "";
      const hasPetNameInDetails = details
        .toLowerCase()
        .includes(petData.name.toLowerCase());

      const itemPetName =
        service.item?.pet_name ||
        service.item?.appointment_info?.pet_details?.name ||
        service.item?.boarding_info?.pet_details?.name;
      const hasPetNameInItem =
        itemPetName?.toLowerCase() === petData.name.toLowerCase();

      const rawPetName =
        service.raw?.pet_name || service.raw?.items?.[0]?.pet_name;
      const hasPetNameInRaw =
        rawPetName?.toLowerCase() === petData.name.toLowerCase();

      const boardingPetName = service.item?.boarding_info?.pet_details?.name;
      const hasBoardingPetInfo =
        boardingPetName?.toLowerCase() === petData.name.toLowerCase();

      const appointmentPetName = service.appointment_info?.pet_details?.name;
      const hasAppointmentPetInfo =
        appointmentPetName?.toLowerCase() === petData.name.toLowerCase();

      return (
        hasPetNameInDetails ||
        hasPetNameInItem ||
        hasPetNameInRaw ||
        hasBoardingPetInfo ||
        hasAppointmentPetInfo
      );
    });

    const orderItems = [];
    clientOrders.forEach((order) => {
      (order.items || []).forEach((item) => {
        const petNameInItem =
          item.pet_details?.name ||
          item.appointment_info?.pet_details?.name ||
          item.boarding_info?.pet_details?.name ||
          item.pet_name;

        if (petNameInItem?.toLowerCase() === petData.name.toLowerCase()) {
          let itemStatus = item.status || order.status;

          if (item.type === "boarding" && item.boarding_info?.status) {
            itemStatus = item.boarding_info.status;
          } else if (item.appointment_info?.status) {
            itemStatus = item.appointment_info.status;
          }

          orderItems.push({
            ...item,
            _id: `${order._id}_${item._id}`,
            order_number: order.order_number,
            order_type: order.order_type,
            createdAt: order.createdAt,
            updatedAt: order.updatedAt,
            total_price: item.total_price || item.price,
            status: itemStatus,
            isOrderItem: true,
            type: item.type || "service",
            payment_status: order.payment_status,
            total_amount: order.total_amount,
            order_amount: order.grand_total,
            total_paid_amount: order.total_paid_amount,
            appointment_info: item.appointment_info,
            boarding_info: item.boarding_info,
            pet_details: item.pet_details,
            service_name: item.service_name,
            service_type_name: item.service_type_name,
            start_date: item.start_date,
            start_time: item.start_time,
            duration: item.duration,
            employee_name: item.employee_name,
          });
        }
      });
    });

    const allItems = [...filteredServices, ...orderItems];

    const uniqueMap = new Map();
    allItems.forEach((item) => {
      const rawOrderNum =
        item.order_number || item.orderNumber || item._id || "";
      const baseOrderNum = rawOrderNum.toString().replace(/-[A-Z]\d+$/, "");

      const itemData = item.item || item;
      const serviceType = itemData.type || "";
      const serviceName =
        itemData.appointment_info?.grooming_services?.name ||
        itemData.boarding_info?.boarding_services?.name ||
        itemData.appointment_info?.vetclinic_services?.name ||
        itemData.service_name ||
        item.details ||
        "";
      const serviceDate =
        itemData.appointment_info?.appointment_date ||
        itemData.boarding_info?.start_date ||
        itemData.start_date ||
        item.date ||
        "";
      const serviceTime =
        itemData.appointment_info?.time_slot ||
        itemData.boarding_info?.time_slot ||
        itemData.start_time ||
        item.time ||
        "";

      const key = `${baseOrderNum}::${serviceType}::${serviceName}::${serviceDate}::${serviceTime}`;

      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, item);
      } else {
        const existing = uniqueMap.get(key);
        const existingOrderNum = (
          existing.order_number ||
          existing.orderNumber ||
          existing._id ||
          ""
        ).toString();
        const isExistingSplit = existingOrderNum !== baseOrderNum;
        const isNewSplit = rawOrderNum.toString() !== baseOrderNum;

        if (isExistingSplit && !isNewSplit) {
          uniqueMap.set(key, item);
        }
      }
    });
    const uniqueItems = Array.from(uniqueMap.values());
    
    // Enrich items with order_amount from clientOrders
    const enrichedItems = uniqueItems.map((item) => {
      if (item.order_amount !== undefined && item.order_amount !== null) {
        return item; // already has order_amount (e.g., from orderItems)
      }

      // Determine the order number from the item (handle both naming conventions)
      const orderNumber = item.order_number || item.orderNumber || null;
      if (!orderNumber) return item; // cannot find order, leave as is

      // Find the matching order in clientOrders
      const matchingOrder = clientOrders.find(
        (order) => order.order_number === orderNumber
      );

      if (matchingOrder) {
        // Clone item to avoid mutating original and add order_amount
        return {
          ...item,
          order_amount: matchingOrder.grand_total,
        };
      }

      return item;
    });

    return enrichedItems.sort((a, b) => {
      const getDate = (item) => {
        if (item.isOrderItem) {
          return item.updatedAt || item.createdAt || new Date(0);
        }
        return (
          item.item?.appointment_info?.appointment_date ||
          item.item?.boarding_info?.start_date ||
          item.start_date ||
          item.date ||
          item.createdAt ||
          new Date(0)
        );
      };
      const dateA = new Date(getDate(a));
      const dateB = new Date(getDate(b));
      return dateB - dateA;
    });
  };

  const handleBoardingSummaryClick = (serviceDetails, e) => {
    e.stopPropagation();

    if (serviceDetails.type?.toLowerCase() !== "boarding") return;

    const serviceRefId =
      serviceDetails.refId ||
      serviceDetails.itemData?.ref_id ||
      serviceDetails.boarding_info?.ref_id ||
      serviceDetails.itemData?._id ||
      serviceDetails.itemId;

    if (!serviceRefId) {
      console.error(
        "Boarding activity summary: service reference ID not found",
        serviceDetails,
      );
      return;
    }

    setSelectedBoardingBooking({
      serviceRefId,
      petName: serviceDetails.petName || petData?.name,
      serviceDetails,
    });
  };

  const allPetServices = getPetAppointmentsAndBoarding();

  const filteredPetServices = allPetServices.filter((service) => {
    const rawStatus = normalizeServiceStatus(
      service.item?.appointment_info?.status ||
        service.item?.boarding_info?.status ||
        service.appointment_info?.status ||
        service.boarding_info?.status ||
        service.status,
    );

    if (!selectedStatus) {
      if (rawStatus === "booked") return false;
    } else {
      switch (selectedStatus) {
        case "confirmed":
          if (rawStatus !== "confirmed") return false;
          break;
        case "started":
          if (rawStatus !== "started") return false;
          break;
        case "completed":
          if (!["completed", "closed", "checkout"].includes(rawStatus))
            return false;
          break;
        case "cancelled":
          if (!["cancelled", "cancel", "no show"].includes(rawStatus))
            return false;
          break;
        default:
          break;
      }
    }

    if (dateFilterState.start || dateFilterState.end) {
      const serviceDate =
        service.item?.appointment_info?.appointment_date ||
        service.item?.boarding_info?.start_date ||
        service.appointment_info?.appointment_date ||
        service.boarding_info?.start_date ||
        service.start_date ||
        service.date ||
        service.createdAt;

      if (!serviceDate) return true;

      const svcDate = new Date(serviceDate);

      if (dateFilterState.start) {
        const from = new Date(dateFilterState.start);
        from.setHours(0, 0, 0, 0);
        if (svcDate < from) return false;
      }
      if (dateFilterState.end) {
        const to = new Date(dateFilterState.end);
        to.setHours(23, 59, 59, 999);
        if (svcDate > to) return false;
      }
    }

    return true;
  });

  const handleStatusUpdate = async (service, newStatus, index) => {
    const result = await Swal.fire({
      title: "Update Status?",
      text: `Do you want to change the status to "${newStatus}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    });

    if (!result.isConfirmed) return;

    if (onStatusUpdate) {
      onStatusUpdate(service, newStatus, index);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getServiceType = (service) => {
    return (
      service.item?.service_type_name ||
      service.service_type_name ||
      service.raw?.service_type_name ||
      ""
    );
  };

  const getServiceDetails = (service) => {
    if (service.isOrderItem) {
      const isProduct = service.type === "product";

      let petName = petData?.name || "Unknown Pet";

      if (service.appointment_info?.pet_details?.name) {
        petName = service.appointment_info.pet_details.name;
      } else if (service.boarding_info?.pet_details?.name) {
        petName = service.boarding_info.pet_details.name;
      } else if (service.pet_details?.name) {
        petName = service.pet_details.name;
      } else if (service.pet_name) {
        petName = service.pet_name;
      }

      let serviceName = service.service_name || "Service";
      if (service.appointment_info?.grooming_services?.name) {
        serviceName = service.appointment_info.grooming_services.name;
      } else if (service.appointment_info?.vetclinic_services?.name) {
        serviceName = service.appointment_info.vetclinic_services.name;
      } else if (service.boarding_info?.boarding_services?.name) {
        serviceName = service.boarding_info.boarding_services.name;
      }

      let serviceTypeName = service.service_type_name || "";
      if (
        service.appointment_info?.grooming_services?.service_category
          ?.service_type_name
      ) {
        serviceTypeName =
          service.appointment_info.grooming_services.service_category
            .service_type_name;
      } else if (
        service.appointment_info?.vetclinic_services?.service_category
          ?.service_type_name
      ) {
        serviceTypeName =
          service.appointment_info.vetclinic_services.service_category
            .service_type_name;
      } else if (
        service.boarding_info?.boarding_services?.service_category
          ?.service_type_name
      ) {
        serviceTypeName =
          service.boarding_info.boarding_services.service_category
            .service_type_name;
      }

      let startTime = null;
      if (service.appointment_info?.time_slot) {
        startTime = service.appointment_info.time_slot;
      } else if (service.start_time) {
        startTime = service.start_time;
      }

      let duration = null;
      if (service.appointment_info?.duration) {
        duration = service.appointment_info.duration;
      } else if (service.duration) {
        duration = service.duration;
      }

      let employeeName = null;
      if (service.appointment_info?.employee_details?.name) {
        employeeName = service.appointment_info.employee_details.name;
      } else if (service.employee_name) {
        employeeName = service.employee_name;
      }

      return {
        type: service.type || "service",
        serviceName: serviceName,
        serviceTypeName: serviceTypeName,
        petName: petName,

        startDate:
          service.appointment_info?.appointment_date ||
          service.boarding_info?.start_date ||
          service.start_date ||
          service.createdAt,

        endDate: service.boarding_info?.end_date || null,

        startTime: startTime,

        duration: duration,

        employeeName: employeeName,

        totalPrice:
          service.order_amount || service.total_price || service.price || 0,

        status: normalizeServiceStatus(
          service.appointment_info?.status ||
            service.boarding_info?.status ||
            service.status ||
            "booked",
        ),

        order_number: (service.orderNumber || service.order_number || "")
          .toString()
          .replace(/-[A-Z]\d+$/, ""),
        order_type: service.order_type,

        payment_status: service.payment_status || "unpaid",

        isOrderItem: true,

        includes: [],
        addons: [],

        roomName: service.boarding_info?.room_details?.room_name || null,

        roomNumber:
          service.boarding_info?.room_details?.room_numbers?.[0]?.room_number ||
          null,

        refId:
          service.ref_id ||
          service.boarding_info?.ref_id ||
          service._id ||
          null,

        itemId: service._id || null,

        itemData: service,

        appointment_info: service.appointment_info,
        boarding_info: service.boarding_info,

        rawServiceData: service,
      };
    }
    if (
      service.item &&
      (service.item.appointment_info || service.item.boarding_info)
    ) {
      const itemType = service.item.type?.toLowerCase();

      let serviceTypeName = "";
      let serviceCategoryTypeId = null;
      let serviceTypeId = null;
      let serviceId = null;

      if (
        itemType === "boarding" &&
        service.item.boarding_info?.boarding_services?.service_category
      ) {
        serviceTypeName =
          service.item.boarding_info.boarding_services.service_category
            .service_type_name;
      } else if (
        itemType === "grooming" &&
        service.item.appointment_info?.grooming_services?.service_category
      ) {
        serviceTypeName =
          service.item.appointment_info.grooming_services.service_category
            .service_type_name;
      } else if (
        itemType === "vet_clinic" &&
        service.item.appointment_info?.vetclinic_services?.service_category
      ) {
        serviceTypeName =
          service.item.appointment_info.vetclinic_services.service_category
            .service_type_name;
      } else {
        serviceTypeName =
          service.item?.service_type_name ||
          service.service_type_name ||
          service.raw?.service_type_name ||
          "";
      }

      if (
        itemType === "grooming" &&
        service.item.appointment_info?.grooming_services
      ) {
        serviceCategoryTypeId =
          service.item.appointment_info.grooming_services.service_category
            ?.service_category_type_id;
        serviceTypeId =
          service.item.appointment_info.grooming_services.service_type?._id;
        serviceId = service.item.appointment_info.grooming_services._id;
      } else if (
        itemType === "boarding" &&
        service.item.boarding_info?.boarding_services
      ) {
        serviceCategoryTypeId =
          service.item.boarding_info.boarding_services.service_category
            ?.service_category_type_id;
        serviceTypeId =
          service.item.boarding_info.boarding_services.service_type?._id;
        serviceId = service.item.boarding_info.boarding_services._id;
      }

      let serviceName =
        service.details ||
        service.item?.service_name ||
        service.item?.appointment_info?.grooming_services?.name ||
        service.item?.boarding_info?.boarding_services?.name ||
        service.service_name ||
        "Service";

      let startDate = "";
      let startTime = "";
      let duration = "";
      let roomName = "N/A";
      let roomNumber = "";
      let endDate = "";

      if (itemType === "grooming" && service.item.appointment_info) {
        serviceName =
          service.item.appointment_info.grooming_services?.name || serviceName;
        startDate =
          service.item.appointment_info.appointment_date ||
          service.date ||
          service.start_date;
        startTime =
          service.item.appointment_info.time_slot ||
          service.time ||
          service.start_time ||
          "";
        duration = service.item.appointment_info.duration || "1 hr";
      } else if (itemType === "boarding" && service.item.boarding_info) {
        serviceName =
          service.item.boarding_info.boarding_services?.name || serviceName;
        startDate =
          service.item.boarding_info.start_date ||
          service.date ||
          service.start_date;
        startTime =
          service.item.boarding_info.time_slot ||
          service.time ||
          service.start_time ||
          "";
        duration = service.item.boarding_info.duration || "1";
        endDate = service.item.boarding_info.end_date || service.end_date || "";
        roomName =
          service.item.boarding_info.room_details?.room_name ||
          service.room_name ||
          "N/A";
        roomNumber =
          service.item.boarding_info.room_details?.room_numbers?.[0]
            ?.room_number ||
          service.room_number ||
          "";
      }

      let petInfo = {};
      let petBreed = "";
      let petSize = "";

      if (
        itemType === "grooming" &&
        service.item.appointment_info?.pet_details
      ) {
        petInfo = service.item.appointment_info.pet_details;
        petBreed = petInfo.breed_info?.breed_name || service.pet_breed || "";
        petSize = petInfo.size_info?.size_label || service.pet_size || "";
      } else if (
        itemType === "boarding" &&
        service.item.boarding_info?.pet_details
      ) {
        petInfo = service.item.boarding_info.pet_details;
        petBreed = petInfo.breed_info?.breed_name || service.pet_breed || "";
        petSize = petInfo.size_info?.size_label || service.pet_size || "";
      }

      const employeeName =
        service.item.appointment_info?.employee_details?.name ||
        service.item?.employee_name ||
        "";

      return {
        type: itemType,
        serviceName: serviceName,
        serviceTypeName: serviceTypeName,
        service_category_type_id: serviceCategoryTypeId,
        service_type_id: serviceTypeId || service.service_type_id,
        serviceId: serviceId || service.service_id,
        vendor_service_id:
          service.item.appointment_info?.vendor_service_id ||
          serviceId ||
          service.service_id,
        petName:
          petInfo.name || service.pet_name || petData?.name || "Unknown Pet",
        petBreed: petBreed,
        petSize: petSize,
        roomName: roomName,
        roomNumber: roomNumber,
        startDate: startDate,
        endDate: endDate,
        startTime: startTime,
        duration: duration,
        unitPrice:
          service.item.unit_price || service.item.price || service.total || 0,
        totalPrice:
          service.order_amount ||
          service.item.total_price ||
          service.item.total_amount ||
          service.total ||
          0,
        status: normalizeServiceStatus(
          service.item.appointment_info?.status ||
            service.item.boarding_info?.status ||
            service.item.status ||
            service.status ||
            "booked",
        ),
        payment_status:
          service.payment_status || service.raw?.payment_status || "unpaid",
        employeeName: employeeName,
        includes: service.item.includes || [],
        serviceId: service.raw?._id || service.order_id,
        itemId: service.item._id,
        refId: service.item.ref_id || service.ref_id,
        addons: service.item.addons || service.addons || [],
        order_number: (service.orderNumber || service.order_number || "")
          .toString()
          .replace(/-[A-Z]\d+$/, ""),
        order_type: service.raw?.order_type || service.order_type || "offline",
        rawServiceData: service.raw || service,
        itemData: service.item,
        item_type: itemType,
        appointment_info: service.item.appointment_info,
        boarding_info: service.item.boarding_info,
        service_type_name: serviceTypeName,
      };
    } else {
      const isBoarding =
        service.details?.toLowerCase().includes("boarding") ||
        service.serviceType?.toLowerCase() === "boarding" ||
        service.type?.toLowerCase() === "boarding" ||
        service.isBoarding;

      const itemType =
        service.item?.type?.toLowerCase() ||
        service.type?.toLowerCase() ||
        (isBoarding ? "boarding" : "grooming");

      let serviceTypeName = "";

      if (
        service.item?.boarding_info?.boarding_services?.service_category
          ?.service_type_name
      ) {
        serviceTypeName =
          service.item.boarding_info.boarding_services.service_category
            .service_type_name;
      } else if (
        service.item?.appointment_info?.grooming_services?.service_category
          ?.service_type_name
      ) {
        serviceTypeName =
          service.item.appointment_info.grooming_services.service_category
            .service_type_name;
      } else if (
        service.item?.appointment_info?.vetclinic_services?.service_category
          ?.service_type_name
      ) {
        serviceTypeName =
          service.item.appointment_info.vetclinic_services.service_category
            .service_type_name;
      } else {
        serviceTypeName =
          service.service_type_name || service.item?.service_type_name || "";
      }

      const serviceName =
        service.details ||
        service.item?.service_name ||
        service.service_name ||
        "Service";

      return {
        type: itemType,
        serviceName: serviceName,
        serviceTypeName: serviceTypeName,
        service_category_type_id: service.service_category_type_id,
        service_type_id: service.service_type_id,
        serviceId: service.raw?._id || service.order_id || service.service_id,
        vendor_service_id: null,
        petName: service.pet_name || petData?.name || "Unknown Pet",
        petBreed: service.pet_breed || "",
        petSize: service.pet_size || "",
        roomName: service.room_name || "N/A",
        roomNumber: service.room_number || "",
        startDate: service.date || service.start_date,
        startTime: service.time || service.start_time || "",
        duration: isBoarding
          ? service.duration || "1"
          : service.duration || "1 hr",
        unitPrice: service.total || service.total_price || 0,
        totalPrice:
          service.order_amount || service.total || service.total_price || 0,
        status: normalizeServiceStatus(service.status || "booked"),
        payment_status: service.payment_status || "unpaid",
        employeeName: "",
        includes: service.includes || [],
        itemId: service.raw?._id || service.order_id,
        refId: service.ref_id || "",
        addons: service.addons || [],
        order_number: (service.orderNumber || service.order_number || "")
          .toString()
          .replace(/-[A-Z]\d+$/, ""),
        order_type: service.order_type || "offline",
        rawServiceData: service.raw || service,
        itemData: service.item,
        item_type: itemType,
        service_type_name: serviceTypeName,
      };
    }
  };

  const getOrderTypeColor = (orderType) => {
    switch (orderType?.toLowerCase()) {
      case "online":
        return "bg-green-100 text-green-700";
      case "offline":
        return "bg-red-100 text-red-700";
      case "enquiry":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
        return "bg-blue-500 text-white";
      case "started":
        return "bg-purple-500 text-white";
      case "completed":
        return "bg-green-600 text-white";
      case "cancelled":
        return "bg-red-600 text-white";
      case "pending":
        return "bg-yellow-500 text-white";
      case "approved":
        return "bg-green-500 text-white";
      case "rejected":
        return "bg-red-600 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const getPaymentBadgeClass = (paymentStatus) => {
    switch (paymentStatus?.toLowerCase()) {
      case "paid":
        return "bg-green-500 text-white";
      case "partial":
        return "bg-yellow-500 text-white";
      default:
        return "bg-red-500 text-white";
    }
  };

  const handleEditAppointment = async (serviceDetails, index) => {
    const isValidObjectId = (id) => {
      return id && typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id);
    };

    const appointmentInfo =
      serviceDetails.appointment_info || serviceDetails.boarding_info || {};

    const vendorServiceId =
      serviceDetails.vendor_service_id ||
      appointmentInfo.vendor_service_id ||
      appointmentInfo.grooming_services?._id ||
      appointmentInfo.boarding_services?._id ||
      serviceDetails.service_id;

    const serviceName =
      appointmentInfo.grooming_services?.name ||
      appointmentInfo.boarding_services?.name ||
      serviceDetails.service_name ||
      serviceDetails.serviceName ||
      serviceDetails.details ||
      "Service";

    const appointmentDate =
      appointmentInfo.appointment_date ||
      appointmentInfo.start_date ||
      serviceDetails.start_date ||
      serviceDetails.appointment_date ||
      serviceDetails.date;

    const timeSlot =
      appointmentInfo.time_slot ||
      serviceDetails.start_time ||
      serviceDetails.appointment_time ||
      serviceDetails.time;

    const duration = appointmentInfo.duration || serviceDetails.duration;

    const employeeId =
      appointmentInfo.employee_id || serviceDetails.employee_id;
    const employeeName =
      appointmentInfo.employee_details?.name ||
      serviceDetails.employee_name ||
      "Staff";

    const editData = {
      vendor_service_id: vendorServiceId,
      service_id: serviceDetails.service_id || vendorServiceId,
      service_category_type_id: serviceDetails.service_category_type_id,
      service_type_name:
        serviceDetails.service_type_name ||
        serviceDetails.serviceTypeName ||
        "Grooming",
      service_type_id: serviceDetails.service_type_id,
      item_type: serviceDetails.item_type || serviceDetails.type || "grooming",
      service_name: serviceName,
      start_date: appointmentDate,
      start_time: timeSlot,
      duration: duration,
      price: serviceDetails.price || serviceDetails.unit_price,
      total_price: serviceDetails.total_price,
      employee_id: employeeId,
      employee_name: employeeName,
      technician_id: employeeId,
      appointment_date: appointmentDate,
      appointment_time: timeSlot,
      includes: serviceDetails.includes || [],
      addons: serviceDetails.addons || [],
      order_number: serviceDetails.order_number,
      ref_id: serviceDetails.refId,
      item_id: serviceDetails.itemId,
      rawServiceData: serviceDetails.rawServiceData,
      itemData: serviceDetails.itemData,
      appointment_info: serviceDetails.appointment_info,
      boarding_info: serviceDetails.boarding_info,
      _hasValidServiceId: !!vendorServiceId,
    };

    if (onEditAppointment) {
      onEditAppointment(editData, index);
    } else {
      console.error("❌ ERROR: onEditAppointment prop is not available");
    }
  };

  const handleDeleteAppointment = (service, index) => {
    if (onDeleteAppointment) {
      onDeleteAppointment(service, index);
    }
  };

  const handleOpenAddons = (service, index) => {
    if (onOpenAddons) {
      onOpenAddons(service, index);
    }
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-2">
      {/* ── Filter Bar ── */}
      <div className="px-4 pt-3 pb-2 bg-white border-b border-gray-100 sticky top-0 z-20 relative">
        <div className="flex flex-nowrap items-center gap-2">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 h-9 flex-shrink-0"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>

          <div className="flex-shrink-0">
            <FilterBar
              showDatePicker={true}
              dateFilterState={dateFilterState}
              onDateChange={handleDateChange}
              onClearDateFilter={handleClearDateFilter}
              activeRange={activeRange}
              onRangeChange={handleRangeChange}
              rangeOptions={ALL_RANGE_OPTIONS}
              filters={[]}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4 px-4 py-4">
        {filteredPetServices.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📅</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Services Found
            </h3>
            <p className="text-gray-500 mb-4">
              No appointments or boarding services found for{" "}
              {petData?.name || "this pet"}.
            </p>
          </div>
        ) : null}
        {filteredPetServices.map((service, index) => {
          const serviceDetails = getServiceDetails(service);
          const serviceType = getServiceType(service);
          const isBoarding = serviceDetails.type === "boarding";

          return (
            <div
              key={service._id || index}
              className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${
                isBoarding ? "hover:border-indigo-300 hover:shadow-md transition-all" : ""
              }`}
            >
              <div className="px-4 py-3">
                {/* Row 1: Date + Order Badge (left side) */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-sm font-semibold text-gray-900">
                      {serviceDetails.startDate
                        ? formatDate(serviceDetails.startDate)
                        : "Date not selected"}
                    </div>
                    {serviceDetails.order_number && (
                      <span
                        className="text-xs font-medium px-2 py-1 rounded-full text-black"
                        style={{
                          backgroundColor:
                            serviceDetails.order_type === "online"
                              ? "#91D06C"
                              : "#A9CCE3",
                        }}
                      >
                        {serviceDetails.order_number}{" "}
                        {serviceDetails.order_type === "online"
                          ? "Online"
                          : "Offline"}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusBadgeClass(
                        serviceDetails.status,
                      )}`}
                    >
                      {serviceDetails.status}
                    </span>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded-full ${getPaymentBadgeClass(
                        serviceDetails.payment_status,
                      )}`}
                    >
                      {serviceDetails.payment_status === "paid"
                        ? " Paid"
                        : serviceDetails.payment_status === "partial"
                          ? "Partial"
                          : "Unpaid"}
                    </span>
                  </div>
                </div>

                {/* Row 2: Service name + type in brackets */}
                <div className="mb-1">
                  <span className="text-gray-900 font-medium text-lg">
                    {serviceDetails.serviceName}
                  </span>
                  {serviceDetails.serviceTypeName && (
                    <span className="text-gray-500 text-sm ml-1">
                      ({serviceDetails.serviceTypeName})
                    </span>
                  )}
                </div>

                {/* Row 3: Date/Time details */}
                <div className="flex items-center justify-between mt-2">
                  <div className="text-sm text-gray-600 flex-1">
                    {isBoarding ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <FiCalendar className="w-4 h-4 text-blue-500" />
                          <span>
                            {serviceDetails.startDate &&
                              formatShortDate(serviceDetails.startDate)}
                            {serviceDetails.endDate &&
                              ` – ${formatShortDate(serviceDetails.endDate)}`}
                          </span>
                        </div>
                        {serviceDetails.roomName &&
                          serviceDetails.roomName !== "N/A" && (
                            <div className="flex items-center gap-1">
                              <FiHome className="w-4 h-4 text-purple-500" />
                              <span>
                                {serviceDetails.roomName}
                                {serviceDetails.roomNumber &&
                                  ` - ${serviceDetails.roomNumber}`}
                              </span>
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 flex-wrap">
                        {serviceDetails.startTime && (
                          <div className="flex items-center gap-1">
                            <FaClockSolid className="w-4 h-4 text-blue-500" />
                            <span className="font-medium">
                              {serviceDetails.startTime}
                            </span>
                          </div>
                        )}
                        {serviceDetails.duration && (
                          <>
                            <span className="text-gray-500">•</span>
                            <span>{serviceDetails.duration}</span>
                          </>
                        )}
                        {serviceDetails.employeeName && (
                          <>
                            <span className="text-gray-500">•</span>
                            <FaUserTie className="w-4 h-4 text-purple-500" />
                            <span>{serviceDetails.employeeName}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-green-600 font-bold text-right ml-4">
                    ₹ {Number(serviceDetails.totalPrice || 0).toFixed(0)}
                  </div>
                </div>

                {/* Action Buttons - Added Summary button for boarding */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Summary button */}
                    {isBoarding && (
                      <button
                        onClick={(e) => handleBoardingSummaryClick(serviceDetails, e)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors border border-indigo-200"
                      >
                        <FiFileText className="w-3.5 h-3.5" />
                        View Summary
                      </button>
                    )}

                    {/* Service Summary Link for non-boarding services */}
                    {serviceDetails.type === "grooming" && serviceDetails.refId && (
                      <GroomingActivitySummaryDrawer
                        refId={serviceDetails.refId}
                        serviceType="grooming"
                        serviceName={serviceDetails.serviceName}
                        dateLabel={
                          serviceDetails.startDate &&
                          formatShortDate(serviceDetails.startDate)
                        }
                        staffName={serviceDetails.employeeName}
                        petName={serviceDetails.petName || petData?.name}
                        orderNumber={serviceDetails.order_number}
                      />
                    )}
                  </div>
                </div>

                {/* Includes & Addons sections */}
                {serviceDetails.includes &&
                  serviceDetails.includes.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Includes
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {serviceDetails.includes.map((item, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                {serviceDetails.addons && serviceDetails.addons.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Addons
                    </label>
                    <div className="space-y-2">
                      {serviceDetails.addons.map((addon, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between"
                        >
                          <span className="text-sm text-gray-700">
                            {addon.name}
                          </span>
                          <span className="text-sm font-medium text-green-600">
                            ₹ {Number(addon.addon_price || 0).toFixed(0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Boarding Activity Summary Drawer */}
      <BoardingActivitySummaryDrawer
        isOpen={!!selectedBoardingBooking}
        onClose={() => setSelectedBoardingBooking(null)}
        serviceRefId={selectedBoardingBooking?.serviceRefId}
        petName={selectedBoardingBooking?.petName}
        serviceDetails={selectedBoardingBooking?.serviceDetails}
      />
    </div>
  );
};

export default Appointments;